import React, { useState } from 'react';
import { ShieldCheck, MapPin, Link as LinkIcon, Calendar, UserPlus, UserCheck, Settings, Palette } from 'lucide-react';
import { User } from '../../types';
import { api } from '../../services/api';
import styles from './Profile.module.css';

interface ProfileHeaderProps {
  user: User;
  currentUser: User | null;
  activeTab: 'posts' | 'replies' | 'media' | 'likes';
  setActiveTab: (tab: 'posts' | 'replies' | 'media' | 'likes') => void;
  onOpenSettings: () => void;
  onProfileUpdated?: (updated: User) => void;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  user,
  currentUser,
  activeTab,
  setActiveTab,
  onOpenSettings,
  onProfileUpdated,
}) => {
  const [isFollowing, setIsFollowing] = useState(user.is_following || false);
  const [friendStatus, setFriendStatus] = useState(user.friend_status || 'none');

  const isSelf = currentUser?.id === user.id;

  const handleFollowToggle = async () => {
    try {
      const res = await api.toggleFollow(user.id);
      setIsFollowing(res.isFollowing);
    } catch (err: any) {
      alert(err.message || 'Follow request failed');
    }
  };

  const handleFriendToggle = async () => {
    try {
      const res = await api.toggleFriend(user.id);
      setFriendStatus(res.status as any);
    } catch (err: any) {
      alert(err.message || 'Friend action failed');
    }
  };

  const themes = [
    { id: 'neon-indigo', label: 'Neon Indigo' },
    { id: 'emerald-aurora', label: 'Emerald Aurora' },
    { id: 'violet-cyber', label: 'Violet Cyber' },
    { id: 'rose-sunset', label: 'Rose Sunset' },
    { id: 'discord-dark', label: 'Discord Dark' },
  ];

  const handleThemeChange = async (themeId: string) => {
    document.documentElement.setAttribute('data-theme', themeId);
    if (isSelf) {
      try {
        const res = await api.updateProfile({ custom_theme: themeId });
        onProfileUpdated && onProfileUpdated(res.user);
      } catch {}
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await api.uploadMedia(file);
      const updated = await api.updateProfile({ banner_url: res.url });
      onProfileUpdated && onProfileUpdated(updated.user);
    } catch (err: any) {
      alert(err.message || 'Banner upload failed');
    }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await api.uploadMedia(file);
      const updated = await api.updateProfile({ avatar_url: res.url });
      onProfileUpdated && onProfileUpdated(updated.user);
    } catch (err: any) {
      alert(err.message || 'Avatar upload failed');
    }
  };

  return (
    <div className={styles.profileContainer}>
      <div className={styles.banner} style={{ backgroundImage: `url(${user.banner_url})` }}>
        <div className={styles.bannerOverlay} />
        {isSelf && (
          <label style={{ position: 'absolute', top: '16px', right: '16px', background: 'rgba(0,0,0,0.6)', color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '0.82rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Palette size={14} /> Change Banner
            <input type="file" accept="image/*" onChange={handleBannerUpload} style={{ display: 'none' }} />
          </label>
        )}
      </div>

      <div className={styles.profileBody}>
        <div className={styles.avatarRow}>
          <div className={styles.avatarWrapper}>
            <img src={user.avatar_url} alt={user.display_name} className={styles.avatar} />
            {isSelf && (
              <label style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', cursor: 'pointer', opacity: 0.9 }}>
                <Palette size={20} />
                <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ display: 'none' }} />
              </label>
            )}
            <span
              className={styles.statusIndicator}
              style={{
                background:
                  user.status === 'online'
                    ? 'var(--status-online)'
                    : user.status === 'idle'
                    ? 'var(--status-idle)'
                    : user.status === 'dnd'
                    ? 'var(--status-dnd)'
                    : 'var(--status-offline)',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {isSelf ? (
              <button className={styles.editBtn} onClick={onOpenSettings}>
                <Settings size={16} /> Edit Profile & Theme
              </button>
            ) : (
              <>
                <button className={styles.editBtn} onClick={handleFriendToggle}>
                  {friendStatus === 'accepted' ? 'Friends ✓' : friendStatus === 'pending' ? 'Request Sent' : 'Add Friend'}
                </button>
                <button
                  className={styles.editBtn}
                  style={{ background: isFollowing ? 'var(--bg-tertiary)' : 'var(--accent-gradient)', color: '#ffffff' }}
                  onClick={handleFollowToggle}
                >
                  {isFollowing ? 'Following' : 'Follow'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className={styles.displayName}>
          {user.display_name}
          {user.verified && <ShieldCheck size={20} color="var(--accent-primary)" fill="currentColor" />}
          {user.pronouns && <span className={styles.pronouns}>{user.pronouns}</span>}
        </div>

        <div className={styles.metaRow}>
          <span>@{user.username}</span>
          {user.location && (
            <span>
              <MapPin size={14} /> {user.location}
            </span>
          )}
          {user.website && (
            <a href={user.website} target="_blank" rel="noreferrer">
              <LinkIcon size={14} /> {user.website.replace('https://', '')}
            </a>
          )}
          <span>
            <Calendar size={14} /> Joined {new Date(user.created_at).toLocaleDateString()}
          </span>
        </div>

        {user.custom_status_text && (
          <div style={{ marginTop: '8px', fontSize: '0.88rem', fontStyle: 'italic', color: 'var(--accent-primary)' }}>
            "{user.custom_status_text}"
          </div>
        )}

        <div className={styles.bio}>{user.bio}</div>

        <div className={styles.statsRow}>
          <div>
            <span className={styles.statNumber}>{user.following_count || 0}</span>{' '}
            <span className={styles.statLabel}>Following</span>
          </div>
          <div>
            <span className={styles.statNumber}>{user.followers_count || 0}</span>{' '}
            <span className={styles.statLabel}>Followers</span>
          </div>
          <div>
            <span className={styles.statNumber}>{user.friends_count || 0}</span>{' '}
            <span className={styles.statLabel}>Friends</span>
          </div>
        </div>

        {/* Discord Theme Picker Bar */}
        <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: 'var(--text-muted)' }}>
          <Palette size={14} /> Profile Theme:
          {themes.map((t) => (
            <button
              key={t.id}
              onClick={() => handleThemeChange(t.id)}
              style={{
                padding: '3px 8px',
                borderRadius: '12px',
                background: 'var(--bg-tertiary)',
                fontSize: '0.75rem',
                border: user.custom_theme === t.id ? '1px solid var(--accent-primary)' : '1px solid var(--border-subtle)',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Profile Navigation Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'posts' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('posts')}
          >
            Posts
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'media' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('media')}
          >
            Media Grid
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'likes' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('likes')}
          >
            Liked Posts
          </button>
        </div>
      </div>
    </div>
  );
};
